import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import {
  mutationEditorQuestionPreview,
  mutationEditorQuestionUpdate,
} from 'gql/mutations/editor';
import { useQueryLocalEditor } from 'gql/queries/local';
import { getQuestionRecto, getQuestionVerso } from 'questions';

import QuestionPreview from './QuestionPreview';

const QuestionPreviewContainer = ({ course, ...props }) => {
  const { t } = useTranslation();
  const {
    data: {
      editor: { status: editorStatus, questionEditing },
    },
  } = useQueryLocalEditor();
  const [seeRecto, setSeeRecto] = useState(true);
  const handleToggleCard = useCallback(() => setSeeRecto((p) => !p), []);
  useEffect(() => setSeeRecto(true), [questionEditing]);
  const allQuestions = useMemo(
    () =>
      [].concat(
        ...course.chapters.map((chapter) => chapter.questions),
        course.questions,
      ),
    [course],
  );
  const previousQuestion = useMemo(
    () =>
      allQuestions.find(
        (question, index) =>
          allQuestions[index + 1]?.id === questionEditing?.id,
      ),
    [allQuestions, questionEditing],
  );
  const nextQuestion = useMemo(
    () =>
      allQuestions.find(
        (question, index) =>
          allQuestions[index - 1]?.id === questionEditing?.id,
      ),
    [allQuestions, questionEditing],
  );
  const isModalOpen = editorStatus === 'question-preview';
  const rectoComponent = getQuestionRecto(questionEditing?.type);
  const versoComponent = getQuestionVerso(questionEditing?.type);
  const initialValues = useMemo(
    () => ({
      answer: rectoComponent?.defaultAnswer(questionEditing),
    }),
    [questionEditing, rectoComponent],
  );
  const questionWithSolution = useMemo(
    () => ({
      ...questionEditing,
      solution: versoComponent?.defaultSolution(questionEditing),
    }),
    [questionEditing, versoComponent],
  );
  const handleQuestionUpdate = useCallback(() => {
    mutationEditorQuestionUpdate(questionEditing, 'question-preview');
  }, [questionEditing]);
  return (
    <QuestionPreview
      course={course}
      handleQuestionChange={mutationEditorQuestionPreview}
      handleQuestionUpdate={handleQuestionUpdate}
      handleToggleCard={handleToggleCard}
      initialValues={initialValues}
      isModalOpen={isModalOpen}
      nextQuestion={nextQuestion}
      previousQuestion={previousQuestion}
      question={questionWithSolution}
      rectoComponent={rectoComponent}
      seeRecto={seeRecto}
      t={t}
      versoComponent={versoComponent}
      {...props}
    />
  );
};

QuestionPreviewContainer.propTypes = {
  course: PropTypes.object.isRequired,
};

export default QuestionPreviewContainer;
